import { useRef } from 'react';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';

import { BaseCell } from './Cells';
import { CheckboxCell, MoneyCell } from './Cells/CellPatterns';
import { getCellPadding, getDefaultPaddingVariantForContentType } from './constants';
import { ColumnWidths, ListItemRendererProps, Row as RowType } from './types';
import Layout from '../Layout';
import { defaultAccessor, isMoneyColumn } from '../Table/utils';

interface Props<T extends RowType> extends ListItemRendererProps<T> {
  rowOnClick?: (row: T, e: React.MouseEvent) => void;
  rowHref?: (row: T) => string | undefined | null;
}

const Row = <T extends RowType>({
  style,
  data: {
    columns,
    row,
    rowIndex,
    bulkSelectColumnKey,
    selectedIds,
    onRowSelectToggle,
    getRowBackground,
    isRowBulkSelectable
  },
  getColumnWidth,
  rowOnClick,
  rowHref
}: Props<T>) => {
  const history = useHistory();
  const ref = useRef<HTMLDivElement>(null);
  const rowHrefValue = rowHref?.(row);
  const hasRowHref = !!rowHrefValue;

  const columnWidths = columns.reduce<ColumnWidths>((widths, column) => {
    widths[column.key as string] = getColumnWidth(column.key as string);

    return widths;
  }, {});

  const allColumnWidthsMeasured = Object.values(columnWidths).every(Boolean);

  if (!allColumnWidthsMeasured) {
    return null;
  }

  const height = '100%';
  const isRowSelected = selectedIds?.has(row.id);
  const rowSurface = !isRowSelected ? getRowBackground?.(row) : undefined;

  return (
    <div
      className={classNames('Table-row', { 'is-clickable': hasRowHref || rowOnClick })}
      data-row-id={String(row.id)}
      {...(hasRowHref
        ? {
            onClick: e => {
              const url = rowHrefValue;
              if (typeof url === 'string' && url.length > 0) {
                if (e.metaKey) {
                  window.open(url, '_blank');
                } else {
                  history.push(url);
                }
              }
            }
          }
        : rowOnClick
          ? { onClick: e => rowOnClick(row, e), role: 'button' }
          : {})}
      style={{ ...style, minWidth: 'fit-content', height: style.height }}
    >
      <Layout
        __ref={ref}
        direction="row"
        align="center"
        height={height}
        {...(isRowSelected
          ? { color: 'blue-100' as const, hoverColor: 'blue-200' as const }
          : rowSurface
            ? { color: rowSurface.color, hoverColor: rowSurface.hoverColor }
            : {})}
      >
        {columns.map(
          (
            {
              key,
              accessor,
              Cell: CellComponent,
              minWidth,
              flexGrow,
              isMoney: columnIsMoney,
              paddingVariant,
              cellContentType,
              align: columnAlign
            },
            columnIndex
          ) => {
            const isFirstColumn = columnIndex === 0;
            const width = columnWidths[key as string];
            const isBulkSelectColumn = bulkSelectColumnKey !== undefined && key === bulkSelectColumnKey;
            const isChecked = isBulkSelectColumn && selectedIds?.has(row.id);
            const bulkSelectAllowed = isRowBulkSelectable ? isRowBulkSelectable(row) : true;
            const isMoney =
              columnIsMoney ??
              ((!!CellComponent && (CellComponent as React.ComponentType<unknown>) === MoneyCell) ||
                isMoneyColumn(key as string));
            const cellJustify =
              columnAlign === 'center'
                ? 'center'
                : columnAlign === 'end'
                  ? 'flex-end'
                  : isMoney && !isFirstColumn
                    ? 'flex-end'
                    : 'flex-start';
            const isCheckboxCell = !!CellComponent && (CellComponent as React.ComponentType<unknown>) === CheckboxCell;
            const defaultPaddingVariant = getDefaultPaddingVariantForContentType(cellContentType);
            const resolvedPaddingVariant = paddingVariant ?? (isCheckboxCell ? 'compactRight' : defaultPaddingVariant);
            const { paddingLeft, paddingRight } = getCellPadding(resolvedPaddingVariant);
            const useColumnFlex = flexGrow !== undefined && flexGrow > 0;

            if (isBulkSelectColumn && onRowSelectToggle) {
              if (!bulkSelectAllowed) {
                return (
                  <div
                    key={key as string}
                    role="presentation"
                    style={{
                      width,
                      height: '100%',
                      minWidth: minWidth || width,
                      paddingLeft,
                      paddingRight,
                      display: 'flex',
                      alignItems: 'center',
                      flexGrow: flexGrow ?? 0
                    }}
                  />
                );
              }

              return (
                <div
                  key={key as string}
                  role="presentation"
                  style={{
                    ...(useColumnFlex
                      ? { flexGrow, flexShrink: 1, flexBasis: 0, minWidth: minWidth || width }
                      : { width, minWidth: minWidth || width, flexShrink: 0 }),
                    height: '100%',
                    paddingLeft,
                    paddingRight,
                    display: 'flex',
                    alignItems: 'center',
                    cursor: bulkSelectAllowed ? 'pointer' : 'default'
                  }}
                  onClickCapture={e => {
                    if (!bulkSelectAllowed) {
                      return;
                    }
                    e.stopPropagation();
                    e.preventDefault();
                    onRowSelectToggle(row.id, !isChecked, rowIndex, e.nativeEvent.shiftKey);
                  }}
                  onMouseDown={e => e.stopPropagation()}
                >
                  <div role="presentation" style={{ position: 'relative' }}>
                    <CheckboxCell
                      value={isChecked ? 'checked' : 'off'}
                      disabled={!bulkSelectAllowed}
                      onChange={({ value }) => onRowSelectToggle(row.id, value === 'checked', rowIndex, false)}
                    />
                  </div>
                </div>
              );
            }

            return CellComponent ? (
              cellContentType === 'status' ? (
                <div
                  key={key as string}
                  style={{
                    ...(useColumnFlex
                      ? { flexGrow, flexShrink: 1, flexBasis: 0, minWidth: minWidth || width }
                      : {
                          width,
                          minWidth: minWidth || width,
                          flexShrink: 0,
                          ...(flexGrow !== undefined ? { flexGrow } : {})
                        }),
                    height: '100%',
                    paddingLeft,
                    paddingRight,
                    boxSizing: 'border-box',
                    display: 'table',
                    tableLayout: 'fixed'
                  }}
                >
                  <div style={{ display: 'table-cell', verticalAlign: 'middle' }}>
                    <CellComponent {...row} width={width} isFirstColumn={isFirstColumn} />
                  </div>
                </div>
              ) : (
                <Layout
                  __style={
                    useColumnFlex
                      ? { flexShrink: 1, flexBasis: 0, minWidth: minWidth || width, height: '100%' }
                      : { width, minWidth: minWidth || width, height: '100%', flexShrink: 0 }
                  }
                  paddingLeft={paddingLeft}
                  paddingRight={paddingRight}
                  key={key as string}
                  align="center"
                  justify={cellJustify}
                  flexGrow={flexGrow}
                >
                  <CellComponent {...row} width={width} isFirstColumn={isFirstColumn} />
                </Layout>
              )
            ) : (
              <Layout
                paddingLeft={paddingLeft}
                paddingRight={paddingRight}
                key={key as string}
                align="center"
                justify={cellJustify}
                flexGrow={flexGrow}
                __style={
                  useColumnFlex
                    ? { flexShrink: 1, flexBasis: 0, minWidth: minWidth || width, height: '100%' }
                    : { width, height: '100%', flexShrink: 0 }
                }
              >
                <BaseCell
                  value={accessor ? accessor(row) : defaultAccessor(row, key)}
                  align={
                    columnAlign === 'end' && isFirstColumn
                      ? 'start'
                      : columnAlign === 'center' || columnAlign === 'end'
                        ? columnAlign
                        : undefined
                  }
                  isFirstColumn={isFirstColumn}
                />
              </Layout>
            );
          }
        )}
      </Layout>
    </div>
  );
};

export default Row;
